import { useQuery, useQueryClient, QueryKey } from 'react-query';
import { useEffect } from 'react';

type UseStorageStateReturn<T> = [T, (value: T) => void, () => void];

/**
 * A custom hook that manages global state using `react-query`, persists it to `localStorage`,
 * and automatically synchronizes the state across different browser tabs.
 *
 * @param key - The `QueryKey` used for `react-query`. It is also stringified to serve as the `localStorage` key.
 * @param initialValue - The default value to use if nothing is found in cache or `localStorage`.
 * @returns A tuple containing the current state, a setter function, and a deletion function.
 */
export const useStorageState = <T>(
  key: QueryKey,
  initialValue: T,
): UseStorageStateReturn<T> => {
  const queryClient = useQueryClient();
  const storageKey = typeof key === 'string' ? key : JSON.stringify(key);

  const getInitialData = (): T => {
    try {
      const item = window.localStorage.getItem(storageKey);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  };

  const { data } = useQuery<T>(key, getInitialData, {
    initialData: getInitialData,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === storageKey) {
        try {
          const newValue = event.newValue
            ? JSON.parse(event.newValue)
            : initialValue;
          queryClient.setQueryData(key, newValue);
        } catch (error) {
          queryClient.setQueryData(key, initialValue);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, storageKey, queryClient, initialValue]);

  const setValue = (value: T): void => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch (error) {
      // ignore
    }
    queryClient.setQueryData(key, value);
  };

  const deleteItem = () => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (error) {
      // ignore
    }
    queryClient.setQueryData(key, initialValue);
  };

  return [data as T, setValue, deleteItem];
};
